import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, of, tap } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors } from '@recruitee/account-data';
import { filterBoolean, TimeService } from '@recruitee/common';
import { SubscriptionService } from '@recruitee/company-logic';
import { I18nService } from '@recruitee/i18n';
import type { JobSchedulerData } from '@recruitee/offers-types';
import { OfferStatus } from '@recruitee/offers-types';
import { DialogService, DialogWidth, NotificationsController } from '@recruitee/user-interface';
import { deepEqual } from '@recruitee/utils';

import { JobSchedulerAnalytics } from '../analytics/job-scheduler.analytics';
import { JobSchedulerDialogComponent } from '../containers/job-scheduler-dialog/job-scheduler-dialog.component';
import { JobSchedulerDialogData } from '../containers/job-scheduler-dialog/job-scheduler-dialog.types';
import { JobValidationErrors } from '../types/job-validation-errors.types';

enum SchedulerToastVariant {
  'publishDate',
  'closeDate',
  'bothDates',
  'clear',
  'update',
}

enum ConfirmationTextVariant {
  'onlyPublish',
  'onlyClose',
  'bothDates',
  'onlyPublishWithSlots',
  'onlyCloseWithSlots',
  'bothDatesWithSlots',
}

type OpenSchedulerDialogParams = {
  jobId: number;
  validationErrors: JobValidationErrors;
  publish: (schedulerData: JobSchedulerData | null) => Observable<boolean>;
};

@Injectable({
  providedIn: 'root',
})
export class JobSchedulerFacade {
  private readonly notificationTexts: Record<SchedulerToastVariant, string> = {
    [SchedulerToastVariant.publishDate]: this.i18n.translate(
      'app.job_scheduler.toasts.success_publish',
    ),
    [SchedulerToastVariant.closeDate]: this.i18n.translate(
      'app.job_scheduler.toasts.success_close',
    ),
    [SchedulerToastVariant.bothDates]: this.i18n.translate('app.job_scheduler.toasts.success_both'),
    [SchedulerToastVariant.clear]: this.i18n.translate('app.job_scheduler.toasts.success_clear'),
    [SchedulerToastVariant.update]: this.i18n.translate('app.job_scheduler.toasts.success_updated'),
  };

  private readonly closeNowConfirmationTexts: Record<ConfirmationTextVariant, string> = {
    [ConfirmationTextVariant.onlyClose]:
      'app.offers.actions.close.confirm_message.job_scheduled_close',
    [ConfirmationTextVariant.onlyPublish]:
      'app.offers.actions.close.confirm_message.job_scheduled_publish',
    [ConfirmationTextVariant.bothDates]:
      'app.offers.actions.close.confirm_message.job_scheduled_both_dates',
    [ConfirmationTextVariant.onlyCloseWithSlots]:
      'app.offers.actions.close.confirm_message.job_scheduled_close_with_slots',
    [ConfirmationTextVariant.onlyPublishWithSlots]:
      'app.offers.actions.close.confirm_message.job_scheduled_publish_with_slots',
    [ConfirmationTextVariant.bothDatesWithSlots]:
      'app.offers.actions.close.confirm_message.job_scheduled_both_dates_with_slots',
  };

  private readonly dateFormat = 'MMMM DD, YYYY';

  public schedulerData$: Observable<JobSchedulerData>;
  public schedulerActive$: Observable<boolean>;
  public isModified: boolean = false;
  public userTimezone: string = '';
  private _schedulerData$: BehaviorSubject<JobSchedulerData> = new BehaviorSubject(null);
  private _jobStatus$: BehaviorSubject<OfferStatus> = new BehaviorSubject(OfferStatus.Draft);
  private publish: (schedulerData: JobSchedulerData | null) => Observable<boolean>;

  constructor(
    private dialog: DialogService,
    private notifications: NotificationsController,
    private i18n: I18nService,
    private store: Store,
    private jobSchedulerAnalytics: JobSchedulerAnalytics,
    private timeService: TimeService,
    private subscription: SubscriptionService,
  ) {
    this.schedulerData$ = this._schedulerData$.asObservable();

    this.schedulerActive$ = combineLatest([
      this._jobStatus$.asObservable(),
      this.store.select(AbilitiesSelectors.hasOne(Ability.PublishJobs)),
    ]).pipe(
      map(
        ([status, hasAbility]) =>
          hasAbility && status !== OfferStatus.Archived && status !== OfferStatus.Closed,
      ),
    );

    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean)
      .subscribe(({ timezone }) => (this.userTimezone = timezone));
  }

  // eslint-disable-next-line max-lines-per-function
  public open(params: OpenSchedulerDialogParams) {
    const { validationErrors, jobId, publish } = params;
    const currentSchedulerData = this._schedulerData$.value;
    const dialogData = {
      ...{ schedulerData: currentSchedulerData },
      disablePublish: this._jobStatus$.value === OfferStatus.Published,
      userTimezone: this.userTimezone,
      validationErrors,
      jobId,
    };
    this.publish = publish;
    this.openSchedulerDialog(dialogData).subscribe();
  }

  public updateSchedulerData(schedulerData: JobSchedulerData): void {
    if (!deepEqual(schedulerData, this._schedulerData$.value)) {
      const data = schedulerData?.publishDate || schedulerData?.closeDate ? schedulerData : null;
      this.isModified = false;
      this._schedulerData$.next(data);
    }
  }

  public updateJobStatus(status: OfferStatus): void {
    this._jobStatus$.next(status);
  }

  public getCloseConfirmDialogMessage(schedulerData?: JobSchedulerData): string {
    const textsRecord = this.closeNowConfirmationTexts;
    const closeDate = schedulerData?.closeDate || this._schedulerData$.value?.closeDate;
    const publishDate = schedulerData?.publishDate || this._schedulerData$.value?.publishDate;
    const closeDateFormat = closeDate ? moment(closeDate).format(this.dateFormat) : '';
    const publishDateFormat = publishDate ? moment(publishDate).format(this.dateFormat) : '';
    const textVariant = closeDate
      ? publishDate
        ? this.subscription.hasSlotsLimit()
          ? ConfirmationTextVariant.bothDatesWithSlots
          : ConfirmationTextVariant.bothDates
        : this.subscription.hasSlotsLimit()
          ? ConfirmationTextVariant.onlyCloseWithSlots
          : ConfirmationTextVariant.onlyClose
      : this.subscription.hasSlotsLimit()
        ? ConfirmationTextVariant.onlyPublishWithSlots
        : ConfirmationTextVariant.onlyPublish;
    const date = closeDate
      ? publishDate
        ? `${publishDateFormat} - ${closeDateFormat}`
        : closeDateFormat
      : publishDateFormat;

    return this.i18n.translate(textsRecord[textVariant], { date });
  }

  public hasScheduledDates(schedulerData?: JobSchedulerData): boolean {
    const data = schedulerData || this._schedulerData$.value;
    return !!(data?.publishDate || data?.closeDate);
  }

  private showToast(variant: SchedulerToastVariant): void {
    this.notifications.success(this.notificationTexts[variant]);
  }

  private openSchedulerDialog(dialogData: JobSchedulerDialogData): Observable<boolean> {
    return this.dialog
      .openCustom(
        JobSchedulerDialogComponent,
        {
          width: DialogWidth.SmallX,
        },
        dialogData,
      )
      .afterClosed.pipe(
        filterBoolean,
        switchMap(({ save, schedulerData }: { save: boolean; schedulerData: JobSchedulerData }) => {
          if (!save) {
            return of(false);
          }

          const data =
            schedulerData?.publishDate || schedulerData?.closeDate ? schedulerData : null;
          const publishNow = data?.publishDate && this.timeService.isToday(data.publishDate);

          if (!publishNow) {
            this.isModified = true;
            return of(true).pipe(tap(() => this.saveSchedulerData(schedulerData)));
          }

          return this.publish(data).pipe(
            switchMap(published =>
              published
                ? of(true)
                : this.openSchedulerDialog({
                    ...dialogData,
                    schedulerData,
                  }),
            ),
          );
        }),
      );
  }

  private saveSchedulerData(schedulerData: JobSchedulerData) {
    const data = schedulerData?.publishDate || schedulerData?.closeDate ? schedulerData : null;
    const hadDates =
      !!this._schedulerData$.value?.publishDate || !!this._schedulerData$.value?.closeDate;

    this._schedulerData$.next(data);
    this.showToast(this.getToastVariant(data, hadDates));

    const analyticsParams = {
      jobStatus: this._jobStatus$.value,
      hasPublish: !!schedulerData?.publishDate,
      hasClose: !!schedulerData?.closeDate,
    };
    if (!hadDates) this.jobSchedulerAnalytics.trackSchedulePublish(analyticsParams);
  }

  private getToastVariant(
    schedulerData: JobSchedulerData | null,
    hadDates: boolean,
  ): SchedulerToastVariant {
    const updated = schedulerData && hadDates;
    if (updated) {
      return SchedulerToastVariant.update;
    } else if (!schedulerData) {
      return SchedulerToastVariant.clear;
    } else if (schedulerData.publishDate && schedulerData.closeDate) {
      return SchedulerToastVariant.bothDates;
    } else if (schedulerData.publishDate) {
      return SchedulerToastVariant.publishDate;
    } else {
      return SchedulerToastVariant.closeDate;
    }
  }
}
